import type { SyntheticEvent } from 'react';
import { Link } from 'react-router-dom';
import { GitBranch } from 'lucide-react';
import type { TaskStackedOn } from '@shared/types/tasks';
import { useFormatTaskRef, useCurrentProject } from '@/lib/current-project';
import { StatusBadge } from '@/components/StatusBadge';
import { useBoardPath } from '@/navigation/project-paths';
import { cn } from '@/lib/utils';
import { stackedMergedText } from './stacked-on';

/** Stops a press on the link from opening, picking or dragging the card it sits on. */
const stop = (e: SyntheticEvent) => e.stopPropagation();

/**
 * T-14: "Stacked on T-14" for a task that builds on another (a suggestion taken up on a new branch),
 * linking to that task. Once that task's PR is merged, a warning badge says so (the full advice is
 * stackedMergedText, on hover and in the task's banner). `card`: the Board card's compact form.
 */
export function StackedOn({
  stackedOn,
  variant = 'detail',
  onNavigate,
  className,
}: {
  stackedOn: TaskStackedOn;
  variant?: 'card' | 'detail';
  onNavigate?: () => void;
  className?: string;
}) {
  const boardPath = useBoardPath();
  const formatRef = useFormatTaskRef();
  const taskPrefix = useCurrentProject()?.task_prefix;
  const ref = formatRef(stackedOn.number);
  const card = variant === 'card';
  return (
    <span
      className={cn('inline-flex min-w-0 max-w-full flex-wrap items-center gap-1.5', card ? 'text-xs' : 'text-sm', className)}
      data-hint={stackedOn.pr_merged ? stackedMergedText(stackedOn, taskPrefix) : `Its runs start from ${ref}'s branch, and its PR goes against that branch`}
    >
      <GitBranch className={cn('shrink-0 text-muted-foreground', card ? 'size-3' : 'size-3.5')} aria-hidden />
      <span className="text-muted-foreground">Stacked on</span>
      <Link
        to={boardPath(stackedOn.task_id)}
        onClick={(e) => {
          stop(e);
          onNavigate?.();
        }}
        onMouseDown={stop}
        onPointerDown={stop}
        onKeyDown={stop}
        className={cn('min-w-0 truncate hover:underline', card ? 'max-w-[10rem]' : 'max-w-[16rem]')}
      >
        {ref}
        {card ? null : ` ${stackedOn.title}`}
      </Link>
      {stackedOn.pr_merged ? (
        <StatusBadge tone="warning" size="sm" dot>
          {ref} merged
        </StatusBadge>
      ) : null}
    </span>
  );
}
